import { useState } from "react";
import { useLocation, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, useReducedMotion } from "framer-motion";
import { AlertCircle, ArrowRight, Loader2, MessageCircle } from "lucide-react";
import { waLink, DEFAULT_WA_MESSAGE } from "@/lib/site";
import { trackEvent, getStoredUtm } from "@/lib/tracking";

// Netlify proxies this path to https://functions.contabeleza.com.br/site/leads.
const LEADS_ENDPOINT = "/api/leads";

const PROFILES = [
    "Salão de beleza",
    "Barbearia",
    "Clínica de estética / SPA",
    "Esmalteria",
    "Estúdio de tatuagem",
    "Profissional autônomo / parceiro",
    "Outro",
];

const TEAM_SIZES = [
    "Somente eu",
    "2 a 4 profissionais",
    "5 a 10 profissionais",
    "11 a 20 profissionais",
    "Mais de 20 profissionais",
];

const INITIAL = {
    nome: "",
    telefone: "",
    email: "",
    negocio: "",
    perfil: "",
    tamanho_equipe: "",
    mensagem: "",
    lgpd_consent: false,
};

const isValidBrazilianMobile = (value) => /^[1-9]\d[6-9]\d{8}$/.test(value.replace(/\D/g, ""));

const formatBrazilianMobile = (value) => {
    let digits = value.replace(/\D/g, "");
    if (digits.length > 11 && digits.startsWith("55")) digits = digits.slice(2);
    digits = digits.slice(0, 11);
    if (!digits) return "";
    if (digits.length <= 2) return `(${digits}`;
    const ddd = `(${digits.slice(0, 2)}) `;
    const rest = digits.slice(2);
    if (rest.length <= 4) return ddd + rest;
    if (rest.length <= 8) return `${ddd}${rest.slice(0, 4)}-${rest.slice(4)}`;
    return `${ddd}${rest.slice(0, 5)}-${rest.slice(5)}`;
};

const PHONE_ERROR_MESSAGE = "Informe um WhatsApp válido com DDD: (11) 99999-9999.";

const SUBMIT_ERROR_MESSAGE = "Não conseguimos enviar agora. Tente pelo botão de WhatsApp.";

const leadWhatsAppMessage = (lead) =>
    lead.nome && lead.negocio
        ? `${DEFAULT_WA_MESSAGE}\n\nMeu nome é ${lead.nome}, do negócio "${lead.negocio}"${lead.perfil ? ` (${lead.perfil})` : ""}.`
        : DEFAULT_WA_MESSAGE;

const CONFETTI_COLORS = ["#0047BB", "#08104F", "#1EBE5B", "#F5B700", "#FF6B8B", "#7FB3FF"];

// Fixed pseudo-random pieces so the burst looks the same on every render.
const CONFETTI = Array.from({ length: 36 }, (_, i) => {
    const angle = (i / 36) * Math.PI * 2 + ((i * 7) % 5) * 0.1;
    const distance = 90 + ((i * 37) % 110);
    return {
        x: Math.cos(angle) * distance,
        y: Math.sin(angle) * distance - 40,
        rotate: ((i * 53) % 360) - 180,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        size: 6 + ((i * 3) % 5),
        round: i % 3 === 0,
        delay: (i % 6) * 0.02,
    };
});

function ConfettiBurst() {
    return (
        <div aria-hidden="true" className="pointer-events-none absolute left-1/2 top-16 h-0 w-0">
            {CONFETTI.map((piece, i) => (
                <motion.span
                    key={i}
                    className="absolute block"
                    style={{
                        width: piece.size,
                        height: piece.round ? piece.size : piece.size * 1.6,
                        backgroundColor: piece.color,
                        borderRadius: piece.round ? "9999px" : "2px",
                    }}
                    initial={{ x: 0, y: 0, opacity: 1, rotate: 0, scale: 0.4 }}
                    animate={{
                        x: piece.x,
                        y: [0, piece.y, piece.y + 160],
                        opacity: [1, 1, 0],
                        rotate: piece.rotate * 3,
                        scale: 1,
                    }}
                    transition={{ duration: 1.8, delay: piece.delay, ease: "easeOut", times: [0, 0.4, 1] }}
                />
            ))}
        </div>
    );
}

function LeadSuccess({ lead, pathname, onReset }) {
    const reduceMotion = useReducedMotion();
    const message = leadWhatsAppMessage(lead);
    const firstName = lead.nome.trim().split(/\s+/)[0];

    return (
        <motion.div
            data-testid="lead-form-success"
            role="status"
            aria-live="polite"
            initial={reduceMotion ? false : { opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            className="relative flex flex-col items-center overflow-hidden px-2 py-10 text-center"
        >
            {!reduceMotion && <ConfettiBurst />}
            <motion.div
                initial={reduceMotion ? false : { scale: 0, rotate: -20 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 260, damping: 14, delay: 0.1 }}
                className="mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-[#F3EDE6] text-4xl"
            >
                <span role="img" aria-label="Comemoração">
                    🎉
                </span>
            </motion.div>
            <h3 className="font-display text-2xl font-semibold text-[#08104F] sm:text-3xl">
                Recebido{firstName ? `, ${firstName}` : ""}!
            </h3>
            <p className="mt-3 max-w-sm text-sm leading-relaxed text-[#3A4768]">
                Um especialista nosso vai falar com você em breve.
            </p>
            <p className="mt-8 text-xs font-semibold uppercase tracking-wider text-[#3A4768]/70">ou fale já</p>
            <a
                href={waLink(message, pathname)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="btn-lead-success-whatsapp"
                onClick={() => trackEvent("whatsapp_click_lead_success", { page_path: pathname })}
                className="group mt-3 flex items-center justify-center gap-2 rounded-full bg-[#1EBE5B] px-7 py-4 text-sm font-semibold text-white transition-all duration-200 hover:bg-[#17a04c] active:scale-[0.98]"
            >
                <MessageCircle className="h-5 w-5" fill="currentColor" strokeWidth={0} />
                Falar no WhatsApp
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </a>
            <button
                type="button"
                data-testid="btn-lead-success-reset"
                onClick={onReset}
                className="mt-5 text-xs font-medium text-[#0047BB] underline-offset-2 hover:underline"
            >
                Enviar outro contato
            </button>
        </motion.div>
    );
}

export default function LeadForm() {
    const [form, setForm] = useState(INITIAL);
    const [sending, setSending] = useState(false);
    const [phoneError, setPhoneError] = useState("");
    const [submitted, setSubmitted] = useState(null);
    const [submitError, setSubmitError] = useState("");
    const reduceMotion = useReducedMotion();
    const { pathname } = useLocation();

    const set = (key) => (e) => {
        const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setForm((f) => ({ ...f, [key]: value }));
    };

    const setPhone = (e) => {
        setPhoneError("");
        setForm((f) => ({ ...f, telefone: formatBrazilianMobile(e.target.value) }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.lgpd_consent) {
            toast.error("É preciso aceitar a Política de Privacidade para continuar.");
            return;
        }
        if (!isValidBrazilianMobile(form.telefone)) {
            setPhoneError(PHONE_ERROR_MESSAGE);
            document.getElementById("lead-tel")?.focus();
            return;
        }
        setSubmitError("");
        setSending(true);
        try {
            await axios.post(LEADS_ENDPOINT, {
                ...form,
                page_path: pathname,
                utm: getStoredUtm(),
            });
            trackEvent("generate_lead", { perfil: form.perfil, page_path: pathname });
            trackEvent("lead_form_submit", { perfil: form.perfil });
            setSubmitted(form);
            setForm(INITIAL);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setSubmitError(typeof detail === "string" ? detail : SUBMIT_ERROR_MESSAGE);
        } finally {
            setSending(false);
        }
    };

    if (submitted) {
        return <LeadSuccess lead={submitted} pathname={pathname} onReset={() => setSubmitted(null)} />;
    }

    return (
        <form data-testid="form-lead-diagnostic" onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
                <div>
                    <label htmlFor="lead-nome" className="mb-1.5 block text-xs font-semibold text-[#08104F]">
                        Seu nome*
                    </label>
                    <input
                        id="lead-nome"
                        data-testid="input-lead-name"
                        className="field"
                        required
                        value={form.nome}
                        onChange={set("nome")}
                        placeholder="Como podemos te chamar?"
                    />
                </div>
                <div>
                    <label htmlFor="lead-tel" className="mb-1.5 block text-xs font-semibold text-[#08104F]">
                        WhatsApp*
                    </label>
                    <input
                        id="lead-tel"
                        data-testid="input-lead-phone"
                        className="field"
                        required
                        type="tel"
                        inputMode="numeric"
                        autoComplete="tel-national"
                        value={form.telefone}
                        onChange={setPhone}
                        onBlur={() => {
                            if (form.telefone && !isValidBrazilianMobile(form.telefone)) {
                                setPhoneError(PHONE_ERROR_MESSAGE);
                            }
                        }}
                        placeholder="(11) 99999-9999"
                        aria-invalid={Boolean(phoneError)}
                        aria-describedby={phoneError ? "lead-tel-error" : undefined}
                    />
                    {phoneError && (
                        <p id="lead-tel-error" role="alert" className="mt-1.5 text-xs font-medium text-red-600">
                            {phoneError}
                        </p>
                    )}
                </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
                <div>
                    <label htmlFor="lead-email" className="mb-1.5 block text-xs font-semibold text-[#08104F]">
                        E-mail*
                    </label>
                    <input
                        id="lead-email"
                        data-testid="input-lead-email"
                        className="field"
                        required
                        type="email"
                        value={form.email}
                        onChange={set("email")}
                        placeholder="voce@seunegocio.com.br"
                    />
                </div>
                <div>
                    <label htmlFor="lead-negocio" className="mb-1.5 block text-xs font-semibold text-[#08104F]">
                        Nome do estabelecimento*
                    </label>
                    <input
                        id="lead-negocio"
                        data-testid="input-lead-business-name"
                        className="field"
                        required
                        value={form.negocio}
                        onChange={set("negocio")}
                        placeholder="Ex.: Studio Aurora"
                    />
                </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
                <div>
                    <label htmlFor="lead-perfil" className="mb-1.5 block text-xs font-semibold text-[#08104F]">
                        Tipo do negócio*
                    </label>
                    <select
                        id="lead-perfil"
                        data-testid="select-lead-profile"
                        className="field"
                        required
                        value={form.perfil}
                        onChange={set("perfil")}
                    >
                        <option value="" disabled>
                            Selecione
                        </option>
                        {PROFILES.map((p) => (
                            <option key={p} value={p}>
                                {p}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label htmlFor="lead-equipe" className="mb-1.5 block text-xs font-semibold text-[#08104F]">
                        Quantidade de profissionais*
                    </label>
                    <select
                        id="lead-equipe"
                        data-testid="input-lead-team-size"
                        className="field"
                        required
                        value={form.tamanho_equipe}
                        onChange={set("tamanho_equipe")}
                    >
                        <option value="" disabled>
                            Selecione
                        </option>
                        {TEAM_SIZES.map((s) => (
                            <option key={s} value={s}>
                                {s}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div>
                <label htmlFor="lead-msg" className="mb-1.5 block text-xs font-semibold text-[#08104F]">
                    Conte um pouco do seu momento (opcional)
                </label>
                <textarea
                    id="lead-msg"
                    data-testid="input-lead-message"
                    className="field min-h-[96px] resize-y"
                    value={form.mensagem}
                    onChange={set("mensagem")}
                    placeholder="Ex.: quero regularizar meus profissionais parceiros e pagar menos imposto do jeito certo."
                />
            </div>

            <label
                htmlFor="lead-lgpd"
                className="flex cursor-pointer items-start gap-3 rounded-xl border border-[#08104F]/10 bg-[#F3EDE6]/60 p-3.5"
            >
                <input
                    id="lead-lgpd"
                    data-testid="checkbox-lead-lgpd-consent"
                    type="checkbox"
                    checked={form.lgpd_consent}
                    onChange={set("lgpd_consent")}
                    className="mt-0.5 h-4 w-4 shrink-0 accent-[#0047BB]"
                />
                <span className="text-xs leading-relaxed text-[#3A4768]">
                    Autorizo a Contabeleza a usar meus dados para retornar este contato, conforme a{" "}
                    <Link to="/politica-de-privacidade" className="font-medium text-[#0047BB] underline">
                        Política de Privacidade
                    </Link>{" "}
                    (LGPD, Lei nº 13.709/2018).*
                </span>
            </label>

            {submitError && (
                <motion.div
                    key={submitError}
                    data-testid="lead-form-error"
                    role="alert"
                    initial={reduceMotion ? false : { opacity: 0, x: 0 }}
                    animate={reduceMotion ? { opacity: 1 } : { opacity: 1, x: [0, -8, 8, -5, 5, 0] }}
                    transition={{ duration: 0.45 }}
                    className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4"
                >
                    <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
                    <div className="flex-1">
                        <p className="text-sm font-semibold text-red-700">Ops! Seu contato não foi enviado.</p>
                        <p className="mt-1 text-xs leading-relaxed text-red-700/90">{submitError}</p>
                        <a
                            href={waLink(leadWhatsAppMessage(form), pathname)}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="btn-lead-error-whatsapp"
                            onClick={() => trackEvent("whatsapp_click_lead_error", { page_path: pathname })}
                            className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#1EBE5B] px-4 py-2 text-xs font-semibold text-white transition-colors duration-200 hover:bg-[#17a04c]"
                        >
                            <MessageCircle className="h-4 w-4" fill="currentColor" strokeWidth={0} />
                            Falar no WhatsApp
                        </a>
                    </div>
                </motion.div>
            )}

            <button
                type="submit"
                data-testid="btn-lead-form-submit"
                disabled={sending}
                className="group flex w-full items-center justify-center gap-2 rounded-full bg-[#0047BB] px-6 py-4 text-sm font-semibold text-white transition-all duration-200 hover:bg-[#08104F] active:scale-[0.98] disabled:opacity-60"
            >
                {sending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                    <>
                        Começar meu diagnóstico gratuito
                        <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                    </>
                )}
            </button>
            <p className="text-center text-[11px] leading-relaxed text-[#3A4768]/70">
                Um especialista retorna seu contato. Sem compromisso e sem contabilês.
            </p>
        </form>
    );
}
